import { useState } from 'react';
import * as R from 'remeda';
import {
  Autocomplete,
  autocompleteClasses,
  Chip,
  Stack,
  TextField,
} from '@rossum/ui/material';
import { useIntl } from '../../ComponentsContext';
import { FilterComponentProps } from '../types';
import { FilterComponentWrapper } from './FilterComponentWrapper';

export const FilterNumberValues = ({
  filterKey,
  name,
  currentValue,
  confirmFilter,
  onBackClick,
  onClose,
}: FilterComponentProps<number[] | undefined>) => {
  const intl = useIntl();

  const [values, setValues] = useState<number[]>(currentValue ?? []);
  const [inputValue, setInputValue] = useState('');

  const getNumberValues = (array: (string | number)[]) =>
    array.flatMap(v =>
      Number.isNaN(Number(v)) || Number(v) === 0 ? [] : [Number(v)]
    );

  // Include any pending inputValue when submitting
  const getValuesToSubmit = () => {
    const pendingValue = Number(inputValue);
    if (inputValue && !Number.isNaN(pendingValue) && pendingValue !== 0) {
      return R.unique([...values, pendingValue]);
    }
    return values;
  };

  const valuesToSubmit = getValuesToSubmit();

  return (
    <FilterComponentWrapper
      filterKey={filterKey}
      name={name}
      onSubmit={() => {
        if (valuesToSubmit.length > 0) {
          confirmFilter(valuesToSubmit);
          onClose();
        }
      }}
      onBackClick={onBackClick}
      onClose={onClose}
      isSubmitDisabled={valuesToSubmit.length < 1}
    >
      <Stack width={1} spacing={0.5}>
        <Autocomplete
          options={[]}
          multiple
          freeSolo
          limitTags={3}
          getLimitTagsText={more => <Chip size="small" label={`+ ${more}`} />}
          value={values}
          onChange={(_event, allOptions, _type, _reason) => {
            setValues(R.unique(getNumberValues(allOptions)));
          }}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          sx={{
            width: '100%',
            // forced line height to align limitTags Chip with ValueChips
            // TODO: remove it when MUI allow you touch wrapper of limitTags Chip
            [`& .${autocompleteClasses.tag}`]: { lineHeight: 1 },
          }}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              sx={{ width: '100%' }}
              size="small"
              onChange={({ target: { value: newValues } }) => {
                if (newValues.includes(',')) {
                  const splitValues = newValues.split(',').map(v => v.trim());

                  setValues(prev =>
                    R.unique([...prev, ...getNumberValues(splitValues)])
                  );
                  setInputValue('');
                }
              }}
              helperText={intl.formatMessage({
                id: 'containers.filtering.helperText.pasteValues',
              })}
            />
          )}
        />
      </Stack>
    </FilterComponentWrapper>
  );
};
